/**
 * Accommodation BIK Detail Modal
 *
 * Per-worker breakdown behind the IRAS Appendix 8A d2 (Place of Residence)
 * figure. Shows the month-by-month dormitory residency and the prorated
 * rental the company pays per segment, sourced from the Dorm Portal API.
 *
 * Company-owned segments (e.g. the 18 dorm) are shown for transparency but
 * flagged as not-yet-taxable (their valuation is a later phase).
 *
 * Design System: Admin Theme (Red)
 */

import React from 'react';
import {
  Home,
  Building2,
  Calendar,
  DollarSign,
  Info,
  CheckCircle2,
  MinusCircle,
} from 'lucide-react';
import Modal from '../../../../components/UI/Modal';
import LoadingSpinner from '../../../../components/UI/LoadingSpinner';
import { useAccommodationBIKDetail } from '../../../../hooks/useBIKData';
import { formatDate } from '../../../../utils/dateUtils';

const formatMoney = (value) =>
  `$${parseFloat(value || 0).toLocaleString('en-SG', { minimumFractionDigits: 2 })}`;

const AccommodationDetailModal = ({ isOpen, staffId, year, onClose }) => {
  const { data: detail, isLoading, isError } = useAccommodationBIKDetail(
    isOpen ? staffId : null,
    isOpen ? year : null
  );

  const monthly = detail?.monthly || [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Accommodation BIK Breakdown (${year || ''})`}
      size="xl"
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      ) : isError ? (
        <div className="text-center py-12">
          <Info className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to load breakdown</h3>
          <p className="text-sm text-gray-500">Could not load the accommodation breakdown. Please try again.</p>
        </div>
      ) : !detail ? null : (
        <div className="space-y-6">
          {/* Employee header */}
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Home className="w-5 h-5 text-red-600" />
            </div>
            <div className="min-w-0">
              <p className="text-base font-semibold text-gray-900 truncate">
                {detail.employee_name || detail.staff_id}
              </p>
              <p className="text-sm text-gray-500 truncate">
                {detail.staff_id}
                {detail.pass_type ? ` \u2022 ${detail.pass_type}` : ''}
              </p>
            </div>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-500 text-xs font-medium">
                <DollarSign className="w-4 h-4" />
                Total d2 Value
              </div>
              <p className="mt-1 text-xl font-bold text-gray-900 tabular-nums truncate">
                {formatMoney(detail.total_taxable_value)}
              </p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-500 text-xs font-medium">
                <Calendar className="w-4 h-4" />
                Taxable Months
              </div>
              <p className="mt-1 text-xl font-bold text-gray-900 tabular-nums">
                {detail.months_taxable || 0}
              </p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-500 text-xs font-medium">
                <Building2 className="w-4 h-4" />
                Dormitories
              </div>
              <p className="mt-1 text-sm font-medium text-gray-900 truncate">
                {(detail.external_dorms || []).length > 0
                  ? detail.external_dorms.join(', ')
                  : '\u2014'}
              </p>
            </div>
          </div>

          {/* Monthly breakdown */}
          {monthly.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No dormitory residency</h3>
              <p className="text-sm text-gray-500">No dormitory residency was found for {year}.</p>
            </div>
          ) : (
            <>
              {/* Mobile: one card per residency segment — HR scans month → dorm →
                  taxable → pro-rated without sideways scrolling through 7 columns. */}
              <div className="md:hidden space-y-2">
                {monthly.map((m) =>
                  (m.segments || []).map((seg, segIdx) => (
                    <div key={`m-${m.month}-${segIdx}`} className="border border-gray-200 rounded-xl p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-gray-900 truncate min-w-0">
                          {m.month_label}
                        </span>
                        {seg.taxable ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 flex-shrink-0">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Taxable
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-400 flex-shrink-0">
                            <MinusCircle className="w-3.5 h-3.5" />
                            Not taxable
                          </span>
                        )}
                      </div>
                      <div className="mt-2 flex items-center gap-2 min-w-0">
                        <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-gray-900 truncate min-w-0">{seg.dorm_name}</span>
                        {seg.is_company_owned && (
                          <span className="inline-flex px-2 py-0.5 text-[10px] font-medium rounded-full bg-blue-100 text-blue-700 flex-shrink-0">
                            Company-owned
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        {seg.move_in_date || '?'} <span className="text-gray-400 mx-0.5">&rarr;</span> {seg.move_out_date || 'present'}
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-gray-500">Rate</span>
                          <span className="text-gray-900 tabular-nums">{formatMoney(seg.rate_monthly)}/mo</span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-gray-500">Days</span>
                          <span className="text-gray-700 tabular-nums">{seg.days}</span>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between gap-2">
                        <span className="text-xs font-medium text-gray-500">Pro-Rated (d2)</span>
                        <span className={`text-sm tabular-nums ${seg.taxable ? 'font-semibold text-gray-900' : 'text-gray-400'}`}>
                          {formatMoney(seg.prorated_cost)}
                        </span>
                      </div>
                    </div>
                  ))
                )}
                <div className="flex items-center justify-between gap-2 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl">
                  <span className="text-sm font-semibold text-gray-700">Total taxable (d2)</span>
                  <span className="text-sm font-bold text-gray-900 tabular-nums">
                    {formatMoney(detail.total_taxable_value)}
                  </span>
                </div>
              </div>

              {/* Desktop: full month-by-month table */}
              <div className="hidden md:block border border-gray-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="table-header">
                      <tr>
                        <th className="table-header-cell">Month</th>
                        <th className="table-header-cell">Dormitory</th>
                        <th className="table-header-cell">Period</th>
                        <th className="table-header-cell text-right">Monthly Rate</th>
                        <th className="table-header-cell text-right">Days</th>
                        <th className="table-header-cell text-center">Taxable</th>
                        <th className="table-header-cell text-right">Pro-Rated</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {monthly.map((m) =>
                        (m.segments || []).map((seg, segIdx) => (
                          <tr key={`${m.month}-${segIdx}`} className="table-row-dense">
                            <td className="px-4 py-3 whitespace-nowrap">
                              {segIdx === 0 ? (
                                <span className="text-sm font-medium text-gray-900">
                                  {m.month_label}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">&#8627;</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2 min-w-0">
                                <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                <span className="text-sm text-gray-900 truncate min-w-0">
                                  {seg.dorm_name}
                                </span>
                                {seg.is_company_owned && (
                                  <span className="inline-flex px-2 py-0.5 text-[10px] font-medium rounded-full bg-blue-100 text-blue-700 flex-shrink-0">
                                    Company-owned
                                  </span>
                                )}
                              </div>
                            </td>
                          <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                            {seg.move_in_date ? formatDate(seg.move_in_date) : '?'} <span className="text-gray-400 mx-0.5">&rarr;</span> {seg.move_out_date ? formatDate(seg.move_out_date) : 'present'}
                          </td>
                            <td className="px-4 py-3 text-right text-sm text-gray-900 tabular-nums">
                              {formatMoney(seg.rate_monthly)}
                            </td>
                            <td className="px-4 py-3 text-right text-sm text-gray-700 tabular-nums">
                              {seg.days}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {seg.taxable ? (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                                  <CheckCircle2 className="w-3.5 h-3.5" />
                                  Yes
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-400">
                                  <MinusCircle className="w-3.5 h-3.5" />
                                  No
                                </span>
                              )}
                            </td>
                            <td className={`px-4 py-3 text-right text-sm tabular-nums ${
                              seg.taxable ? 'font-medium text-gray-900' : 'text-gray-400'
                            }`}>
                              {formatMoney(seg.prorated_cost)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    <tfoot className="bg-gray-50 border-t border-gray-200">
                      <tr>
                        <td colSpan={6} className="px-4 py-3 text-right text-sm font-semibold text-gray-700">
                          Total taxable (d2)
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-gray-900 tabular-nums">
                          {formatMoney(detail.total_taxable_value)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Footnote */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <div className="flex items-start gap-3 text-sm text-gray-600">
              <Info className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
              <p>
                The d2 value is the prorated rental the company pays for the
                resident&apos;s bed, summed across the tax year. Company-owned
                dormitory segments are shown for context but currently
                contribute $0 &mdash; their valuation basis is handled in a
                later phase.
              </p>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default AccommodationDetailModal;
